import ThemeLoader from './global-loader';

const ORDER_WAITING_MESSAGES = {
  addresses: {
    title: 'Verifying addresses',
    text: 'We are saving your addresses and recalculating your order. Please wait.',
  },
  shipping: {
    title: 'Saving shipping method',
    text: 'We are saving your delivery method and recalculating shipping costs. Please wait.',
  },
} as const;

type OrderWaitingStep = keyof typeof ORDER_WAITING_MESSAGES;

const ORDER_WAITING_SELECTORS = {
  explicitTrigger: '[data-order-waiting-step]',
  addressContinueButton: 'button[name="confirm-addresses"]',
  shippingContinueButton: [
    'button[name="confirmDeliveryOption"]',
    'button[name="confirm-delivery-option"]',
  ].join(', '),
  addressesStep: '#checkout-addresses-step',
  shippingStep: [
    '#checkout-delivery-step',
    '#checkout-shipping-step',
  ].join(', '),
};

const ORDER_WAITING_FALLBACK_MS = 25000;

let orderWaitingInitialized = false;
let orderWaitingToken: string | null = null;
let orderWaitingFallbackTimer: number | null = null;
let orderWaitingShowTimer: number | null = null;
let pendingStep: OrderWaitingStep | null = null;

function clearShowTimer(): void {
  if (orderWaitingShowTimer === null) {
    return;
  }

  window.clearTimeout(orderWaitingShowTimer);
  orderWaitingShowTimer = null;
}

function clearFallbackTimer(): void {
  if (orderWaitingFallbackTimer === null) {
    return;
  }

  window.clearTimeout(orderWaitingFallbackTimer);
  orderWaitingFallbackTimer = null;
}

function getStepFromElement(element: Element): OrderWaitingStep | null {
  const explicitStep = element.getAttribute('data-order-waiting-step');

  if (explicitStep === 'addresses' || explicitStep === 'shipping') {
    return explicitStep;
  }

  if (
    element.matches(ORDER_WAITING_SELECTORS.addressContinueButton)
    || element.closest(ORDER_WAITING_SELECTORS.addressesStep)
  ) {
    return 'addresses';
  }

  if (
    element.matches(ORDER_WAITING_SELECTORS.shippingContinueButton)
    || element.closest(ORDER_WAITING_SELECTORS.shippingStep)
  ) {
    return 'shipping';
  }

  return null;
}

function isDisabledTrigger(element: Element): boolean {
  if (
    (element instanceof HTMLButtonElement || element instanceof HTMLInputElement)
    && element.disabled
  ) {
    return true;
  }

  return element.getAttribute('aria-disabled') === 'true';
}

function isInvalidFormTrigger(element: Element): boolean {
  const form = element.closest('form');

  if (!form || typeof form.checkValidity !== 'function') {
    return false;
  }

  return !form.checkValidity();
}

function showOrderWaitingNow(step: OrderWaitingStep): void {
  const data = ORDER_WAITING_MESSAGES[step];

  if (orderWaitingToken) {
    ThemeLoader.update(
      {
        title: data.title,
        text: data.text,
      },
      orderWaitingToken,
    );
  } else {
    orderWaitingToken = ThemeLoader.show({
      title: data.title,
      text: data.text,
    });
  }

  clearFallbackTimer();

  orderWaitingFallbackTimer = window.setTimeout(() => {
    hideOrderWaiting();
  }, ORDER_WAITING_FALLBACK_MS);
}

function scheduleOrderWaiting(step: OrderWaitingStep): void {
  pendingStep = step;

  if (orderWaitingShowTimer !== null) {
    return;
  }

  /*
   * Loader pokazujemy dopiero po zakończeniu bieżącej obsługi eventu.
   * Nie ingeruje on w requesty, submit formularza ani nawigację.
   */
  orderWaitingShowTimer = window.setTimeout(() => {
    orderWaitingShowTimer = null;

    const stepToShow = pendingStep;
    pendingStep = null;

    if (stepToShow) {
      showOrderWaitingNow(stepToShow);
    }
  }, 0);
}

function hideOrderWaiting(): void {
  clearShowTimer();
  clearFallbackTimer();

  pendingStep = null;

  if (!orderWaitingToken) {
    return;
  }

  ThemeLoader.hide(orderWaitingToken);
  orderWaitingToken = null;
}

function bindClickHandler(): void {
  document.addEventListener(
    'click',
    (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const trigger = event.target.closest([
        ORDER_WAITING_SELECTORS.explicitTrigger,
        ORDER_WAITING_SELECTORS.addressContinueButton,
        ORDER_WAITING_SELECTORS.shippingContinueButton,
      ].join(', '));

      if (!trigger || isDisabledTrigger(trigger) || isInvalidFormTrigger(trigger)) {
        return;
      }

      const step = getStepFromElement(trigger);

      if (!step) {
        return;
      }

      /*
       * Celowo brak preventDefault/stopPropagation/stopImmediatePropagation.
       * Loader jest wyłącznie wizualny.
       */
      scheduleOrderWaiting(step);
    },
    true,
  );
}

function bindSubmitHandler(): void {
  document.addEventListener(
    'submit',
    (event: SubmitEvent) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      if (
        typeof form.checkValidity === 'function'
        && !form.checkValidity()
      ) {
        return;
      }

      let step: OrderWaitingStep | null = null;

      if (form.closest(ORDER_WAITING_SELECTORS.addressesStep)) {
        step = 'addresses';
      } else if (form.closest(ORDER_WAITING_SELECTORS.shippingStep)) {
        step = 'shipping';
      }

      if (step) {
        scheduleOrderWaiting(step);
      }
    },
    true,
  );
}

function bindPrestashopEvents(prestashop: typeof window.prestashop): void {
  if (!prestashop || typeof prestashop.on !== 'function') {
    return;
  }

  [
    'updatedCheckout',
    'changedCheckoutStep',
    'handleError',
  ].forEach((eventName) => {
    prestashop.on(eventName, hideOrderWaiting);
  });
}

function bindPageEvents(): void {
  window.addEventListener('pageshow', hideOrderWaiting);
}

export default function initOrderWaiting(
  prestashop = window.prestashop,
): void {
  if (orderWaitingInitialized) {
    return;
  }

  orderWaitingInitialized = true;

  bindClickHandler();
  bindSubmitHandler();
  bindPrestashopEvents(prestashop);
  bindPageEvents();
}
